import React, { useState, Component } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box, Container, Typography, Paper, TextField, InputAdornment,
  IconButton, Menu, MenuItem, ListItemIcon, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, Chip, Tabs, Tab,
  Select, FormControl, InputLabel, CircularProgress, Alert, Divider
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import RoomServiceIcon from '@mui/icons-material/RoomService'
import CloseIcon from '@mui/icons-material/Close'
import UpgradeIcon from '@mui/icons-material/Upgrade'
import SendIcon from '@mui/icons-material/Send'
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead'
import RestoreIcon from '@mui/icons-material/Restore'
import DataTable from 'react-data-table-component'
import {
  getRestaurantStaff, getInactiveRestaurantStaff, getRestaurantDrivers, getZones,
  createStaff, editStaff, deleteStaff, reactivateStaff,
  resendStaffInvite, promoteDriverToRushMyFood,
  createRider, deleteRider,
  recordStaffPayout, getStaffPayouts
} from '../apollo'

const GET_STAFF = gql`${getRestaurantStaff}`
const GET_INACTIVE_STAFF = gql`${getInactiveRestaurantStaff}`
const GET_DRIVERS = gql`${getRestaurantDrivers}`
const GET_ZONES = gql`${getZones}`
const CREATE_STAFF = gql`${createStaff}`
const EDIT_STAFF = gql`${editStaff}`
const DELETE_STAFF = gql`${deleteStaff}`
const REACTIVATE_STAFF = gql`${reactivateStaff}`
const RESEND_INVITE = gql`${resendStaffInvite}`
const PROMOTE_DRIVER = gql`${promoteDriverToRushMyFood}`
const CREATE_RIDER = gql`${createRider}`
const DELETE_RIDER = gql`${deleteRider}`
const RECORD_PAYOUT = gql`${recordStaffPayout}`
const GET_STAFF_PAYOUTS = gql`${getStaffPayouts}`

// Refetch the three list queries scoped to the restaurant currently being
// viewed. For super admins this is the selected restaurant (localStorage);
// for vendors it's ignored by the backend (they're scoped to their own).
const buildRefetch = restaurantId => {
  const variables = { restaurantId }
  return [
    { query: GET_STAFF, variables },
    { query: GET_INACTIVE_STAFF, variables },
    { query: GET_DRIVERS, variables },
  ]
}

class StaffErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null } }
  static getDerivedStateFromError(error) { return { hasError: true, error } }
  render() {
    if (this.state.hasError) return (
      <Box sx={{ p: 3, color: '#dc2626', bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', m: 3 }}>
        <strong>Staff page error:</strong> {this.state.error?.message || 'Unknown error'}
      </Box>
    )
    return this.props.children
  }
}

const roleColor = { SERVER: '#3b82f6', MANAGER: '#8b5cf6', CASHIER: '#0891b2', KITCHEN: '#f59e0b', DRIVER: '#f97316' }
const roleIcon = {
  SERVER: <RoomServiceIcon sx={{ fontSize: 18 }} />,
  MANAGER: <ManageAccountsIcon sx={{ fontSize: 18 }} />,
  CASHIER: <RoomServiceIcon sx={{ fontSize: 18 }} />,
  KITCHEN: <RoomServiceIcon sx={{ fontSize: 18 }} />,
  DRIVER: <TwoWheelerIcon sx={{ fontSize: 18 }} />,
}

const emptyStaffForm = { firstName: '', lastName: '', email: '', phone: '', address: '', role: ['SERVER'] }
const emptyDriverForm = { name: '', email: '', phone: '' }

function Staff() {
  const [tab, setTab] = useState(0)
  const [search, setSearch] = useState('')

  const [addModal, setAddModal] = useState(false)
  const [editModal, setEditModal] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [form, setForm] = useState(emptyStaffForm)
  const [formError, setFormError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')

  const [addDriverModal, setAddDriverModal] = useState(false)
  const [driverForm, setDriverForm] = useState(emptyDriverForm)
  const [driverFormError, setDriverFormError] = useState('')

  const [promoteModal, setPromoteModal] = useState(false)
  const [promoteRider, setPromoteRider] = useState(null)
  const [promoteZone, setPromoteZone] = useState('')

  // Payout state
  const [payoutModal, setPayoutModal] = useState(false)
  const [payoutStaff, setPayoutStaff] = useState(null)
  const [payoutAmount, setPayoutAmount] = useState('')
  const [payoutMethod, setPayoutMethod] = useState('Cash')
  const [payoutNote, setPayoutNote] = useState('')
  const [payoutError, setPayoutError] = useState('')
  const [historyModal, setHistoryModal] = useState(false)
  const [historyStaff, setHistoryStaff] = useState(null)

  // The restaurant currently being viewed. Super admins set this in
  // localStorage when they open a restaurant from the restaurant list; the
  // backend only honors it for ADMIN tokens, so vendors are unaffected.
  const restaurantId = localStorage.getItem('restaurantId') || null
  const REFETCH = buildRefetch(restaurantId)

  const { data: staffData, loading: staffLoading } = useQuery(GET_STAFF, { variables: { restaurantId } })
  const { data: inactiveData, loading: inactiveLoading } = useQuery(GET_INACTIVE_STAFF, { variables: { restaurantId } })
  const { data: driverData, loading: driverLoading } = useQuery(GET_DRIVERS, { variables: { restaurantId } })
  const { data: zonesData } = useQuery(GET_ZONES)

  const [mutateCreate, { loading: creating }] = useMutation(CREATE_STAFF, { refetchQueries: REFETCH })
  const [mutateEdit, { loading: saving }] = useMutation(EDIT_STAFF, { refetchQueries: REFETCH })
  const [mutateDelete] = useMutation(DELETE_STAFF, { refetchQueries: REFETCH })
  const [mutateReactivate] = useMutation(REACTIVATE_STAFF, { refetchQueries: REFETCH })
  const [mutateResend] = useMutation(RESEND_INVITE, { refetchQueries: REFETCH })
  const [mutatePromote] = useMutation(PROMOTE_DRIVER, { refetchQueries: REFETCH })
  const [mutateCreateRider, { loading: creatingDriver }] = useMutation(CREATE_RIDER, { refetchQueries: REFETCH })
  const [mutateDeleteRider] = useMutation(DELETE_RIDER, { refetchQueries: REFETCH })
  const [mutateRecordPayout, { loading: payingOut }] = useMutation(RECORD_PAYOUT, { refetchQueries: REFETCH })

  const staffList = staffData?.restaurantStaff ?? []
  const inactiveList = inactiveData?.inactiveRestaurantStaff ?? []
  const driverList = driverData?.restaurantDrivers ?? []
  const zones = zonesData?.zones ?? []

  const q = search.toLowerCase()
  const filteredStaff = staffList.filter(s => {
    const roles = (s.role || '').split(',')
    const matchRole = tab === 0 || (tab === 1 && roles.includes('SERVER')) || (tab === 2 && roles.includes('MANAGER')) || (tab === 3 && roles.includes('CASHIER')) || (tab === 4 && roles.includes('KITCHEN')) || (tab === 5 && roles.includes('DRIVER'))
    const matchSearch = !q || `${s.firstName} ${s.lastName} ${s.email} ${s.phone}`.toLowerCase().includes(q)
    return matchRole && matchSearch
  })
  const filteredDrivers = driverList.filter(d => {
    const matchTab = tab === 0 || tab === 5
    const matchSearch = !q || `${d.name} ${d.phone ?? ''}`.toLowerCase().includes(q)
    return matchTab && matchSearch
  })
  const filteredInactive = inactiveList.filter(s =>
    !q || `${s.firstName} ${s.lastName} ${s.email} ${s.phone}`.toLowerCase().includes(q)
  )
  const showStaff = tab !== 5 && tab !== 6
  const showDrivers = tab === 0 || tab === 5
  const showInactive = tab === 6

  // ── Create staff ──────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!form.firstName || !form.lastName || !form.email || !form.phone) {
      setFormError('First name, last name, email and phone are required')
      return
    }
    setFormError('')
    try {
      await mutateCreate({
        variables: {
          restaurantId,
          staffInput: {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            phone: form.phone,
            address: form.address,
            role: form.role.join(','),
          }
        }
      })
      setAddModal(false)
      setForm(emptyStaffForm)
      setSuccessMsg(`Invitation sent to ${form.email}`)
      setTimeout(() => setSuccessMsg(''), 5000)
    } catch (e) {
      setFormError(e.graphQLErrors?.[0]?.message || e.message)
    }
  }

  // ── Edit staff ────────────────────────────────────────────────────────────
  const openEdit = s => {
    setSelectedStaff(s)
    setForm({ firstName: s.firstName, lastName: s.lastName, email: s.email, phone: s.phone, address: s.address || '', role: (s.role || 'SERVER').split(',') })
    setFormError('')
    setEditModal(true)
  }

  const handleEdit = async () => {
    setFormError('')
    try {
      await mutateEdit({
        variables: {
          restaurantId,
          staffInput: {
            _id: selectedStaff._id,
            firstName: form.firstName,
            lastName: form.lastName,
            phone: form.phone,
            address: form.address,
            role: form.role.join(','),
          }
        }
      })
      setEditModal(false)
    } catch (e) {
      setFormError(e.graphQLErrors?.[0]?.message || e.message)
    }
  }

  // ── Resend invite ─────────────────────────────────────────────────────────
  const handleResend = async (id, email) => {
    try {
      await mutateResend({ variables: { id, restaurantId } })
      setSuccessMsg(`Invitation resent to ${email}`)
      setTimeout(() => setSuccessMsg(''), 5000)
    } catch (e) {
      console.error(e)
    }
  }

  // ── Reactivate staff ──────────────────────────────────────────────────────
  const handleReactivate = async (id, name) => {
    try {
      await mutateReactivate({ variables: { id, restaurantId } })
      setSuccessMsg(`${name} has been reactivated`)
      setTimeout(() => setSuccessMsg(''), 5000)
    } catch (e) {
      console.error(e)
    }
  }

  // ── Create driver ─────────────────────────────────────────────────────────
  const handleCreateDriver = async () => {
    if (!driverForm.name || !driverForm.email || !driverForm.phone) {
      setDriverFormError('Name, email and phone are required')
      return
    }
    setDriverFormError('')
    try {
      await mutateCreateRider({
        variables: { riderInput: { name: driverForm.name, email: driverForm.email, phone: driverForm.phone, driverType: 'RESTAURANT', restaurantId } }
      })
      setAddDriverModal(false)
      setDriverForm(emptyDriverForm)
    } catch (e) {
      setDriverFormError(e.message)
    }
  }

  // ── Promote driver ────────────────────────────────────────────────────────
  const handlePromote = async () => {
    if (!promoteZone) return
    try {
      await mutatePromote({ variables: { riderId: promoteRider._id, zoneId: promoteZone, restaurantId } })
      setPromoteModal(false)
      setPromoteRider(null)
      setPromoteZone('')
    } catch (e) {
      console.error(e)
    }
  }

  // ── Record payout ─────────────────────────────────────────────────────────
  const openPayoutModal = (staff) => {
    setPayoutStaff(staff)
    const outstanding = parseFloat(staff.totalTipsEarned || 0) - parseFloat(staff.totalTipsPaid || 0)
    setPayoutAmount(outstanding > 0 ? outstanding.toFixed(2) : '')
    setPayoutMethod('Cash')
    setPayoutNote('')
    setPayoutError('')
    setPayoutModal(true)
  }

  const handleRecordPayout = async () => {
    const amt = parseFloat(payoutAmount)
    if (!payoutAmount || isNaN(amt) || amt <= 0) {
      setPayoutError('Please enter a valid amount greater than 0')
      return
    }
    setPayoutError('')
    try {
      await mutateRecordPayout({
        variables: { staffId: payoutStaff._id, amount: amt, method: payoutMethod, note: payoutNote || null, restaurantId }
      })
      setPayoutModal(false)
      setSuccessMsg(`Payout of $${amt.toFixed(2)} recorded for ${payoutStaff.firstName} ${payoutStaff.lastName}`)
      setTimeout(() => setSuccessMsg(''), 5000)
    } catch (e) {
      setPayoutError(e.graphQLErrors?.[0]?.message || e.message)
    }
  }

  // ── Action menu ───────────────────────────────────────────────────────────
  const ActionMenu = ({ row, isDriver }) => {
    const [anchor, setAnchor] = useState(null)
    return (
      <>
        <IconButton size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ color: '#94a3b8' }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
          PaperProps={{ elevation: 0, sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f1f5f9', minWidth: 190 } }}>
          {!isDriver && (
            <MenuItem onClick={() => { setAnchor(null); openEdit(row) }}
              sx={{ fontSize: '0.83rem', borderRadius: '8px', mx: 0.5, my: 0.25 }}>
              <ListItemIcon><EditIcon fontSize="small" sx={{ color: '#3b82f6' }} /></ListItemIcon>Edit
            </MenuItem>
          )}
          {!isDriver && (
            <MenuItem onClick={() => { setAnchor(null); handleResend(row._id, row.email) }}
              sx={{ fontSize: '0.83rem', borderRadius: '8px', mx: 0.5, my: 0.25 }}>
              <ListItemIcon><SendIcon fontSize="small" sx={{ color: '#f59e0b' }} /></ListItemIcon>
              {row.emailVerified ? 'Reset Password' : 'Resend Invite'}
            </MenuItem>
          )}
          {!isDriver && (
            <MenuItem onClick={() => { setAnchor(null); openPayoutModal(row) }}
              sx={{ fontSize: '0.83rem', borderRadius: '8px', mx: 0.5, my: 0.25 }}>
              <ListItemIcon><Typography sx={{ fontSize: 16 }}>💵</Typography></ListItemIcon>
              Record Payout
            </MenuItem>
          )}
          {!isDriver && (
            <MenuItem onClick={() => { setAnchor(null); setHistoryStaff(row); setHistoryModal(true) }}
              sx={{ fontSize: '0.83rem', borderRadius: '8px', mx: 0.5, my: 0.25 }}>
              <ListItemIcon><Typography sx={{ fontSize: 16 }}>📋</Typography></ListItemIcon>
              Payout History
            </MenuItem>
          )}
          {isDriver && (
            <MenuItem onClick={() => { setAnchor(null); setPromoteRider(row); setPromoteZone(''); setPromoteModal(true) }}
              sx={{ fontSize: '0.83rem', borderRadius: '8px', mx: 0.5, my: 0.25 }}>
              <ListItemIcon><UpgradeIcon fontSize="small" sx={{ color: '#8b5cf6' }} /></ListItemIcon>Promote to RushMyFood
            </MenuItem>
          )}
          <MenuItem
            onClick={() => { setAnchor(null); isDriver ? mutateDeleteRider({ variables: { id: row._id } }) : mutateDelete({ variables: { id: row._id, restaurantId } }) }}
            sx={{ fontSize: '0.83rem', borderRadius: '8px', mx: 0.5, my: 0.25, color: '#ef4444' }}>
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>Remove
          </MenuItem>
        </Menu>
      </>
    )
  }

  // ── Table columns ─────────────────────────────────────────────────────────
  const staffColumns = [
    {
      name: 'NAME', sortable: true, selector: r => r.firstName, minWidth: '200px',
      cell: r => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: `${roleColor[(r.role||'').split(',')[0]]}20`, border: `2px solid ${roleColor[(r.role||'').split(',')[0]]}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: roleColor[(r.role||'').split(',')[0]] }}>
            {roleIcon[(r.role||'').split(',')[0]]}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>{r.firstName} {r.lastName}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{r.email}</Typography>
          </Box>
        </Box>
      )
    },
    { name: 'PHONE', selector: r => r.phone, minWidth: '130px', cell: r => <Typography sx={{ fontSize: '0.83rem', color: '#475569' }}>{r.phone}</Typography> },
    {
      name: 'ROLE', minWidth: '160px',
      cell: r => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(r.role || 'SERVER').split(',').map(role => (
            <Chip key={role} label={role} size="small" sx={{ bgcolor: `${roleColor[role]}15`, color: roleColor[role], fontWeight: 700, fontSize: '0.7rem', border: `1px solid ${roleColor[role]}30` }} />
          ))}
        </Box>
      )
    },
    {
      name: 'STATUS', minWidth: '150px',
      cell: r => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Chip
            icon={r.emailVerified ? <MarkEmailReadIcon sx={{ fontSize: '14px !important' }} /> : undefined}
            label={r.emailVerified ? 'Email Verified' : 'Invite Sent'}
            size="small"
            sx={{ bgcolor: r.emailVerified ? '#f0fdf4' : '#fef9c3', color: r.emailVerified ? '#16a34a' : '#ca8a04', fontWeight: 600, fontSize: '0.68rem' }}
          />
          {r.emailVerified && (
            <Chip label={r.phoneVerified ? 'Phone Verified' : 'Phone Pending'} size="small"
              sx={{ bgcolor: r.phoneVerified ? '#f0fdf4' : '#f1f5f9', color: r.phoneVerified ? '#16a34a' : '#64748b', fontWeight: 600, fontSize: '0.68rem' }} />
          )}
        </Box>
      )
    },
    {
      name: 'TIPS', minWidth: '180px',
      cell: r => {
        const earned = parseFloat(r.totalTipsEarned || 0)
        const paid = parseFloat(r.totalTipsPaid || 0)
        const outstanding = earned - paid
        return (
          <Box sx={{ py: 0.5 }}>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              <Chip size="small" label={`Earned $${earned.toFixed(2)}`}
                sx={{ bgcolor: '#f0fdf4', color: '#15803d', fontWeight: 600, fontSize: '0.65rem', height: 18 }} />
              <Chip size="small" label={`Paid $${paid.toFixed(2)}`}
                sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600, fontSize: '0.65rem', height: 18 }} />
            </Box>
            {outstanding > 0 && (
              <Typography sx={{ fontSize: '0.68rem', color: '#d97706', fontWeight: 700, mt: 0.25 }}>
                Owes ${outstanding.toFixed(2)}
              </Typography>
            )}
          </Box>
        )
      }
    },
    { name: '', cell: r => <ActionMenu row={r} isDriver={false} />, width: '60px', right: true },
  ]

  const driverColumns = [
    {
      name: 'DRIVER', sortable: true, selector: r => r.name, minWidth: '200px',
      cell: r => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#fef3c720', border: '2px solid #f59e0b40', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#f59e0b' }}>
            <TwoWheelerIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>{r.name}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>@{r.username}</Typography>
          </Box>
        </Box>
      )
    },
    { name: 'PHONE', selector: r => r.phone, minWidth: '130px', cell: r => <Typography sx={{ fontSize: '0.83rem', color: '#475569' }}>{r.phone ?? '—'}</Typography> },
    { name: 'STATUS', minWidth: '110px', cell: r => <Chip label={r.available ? 'Available' : 'Offline'} size="small" sx={{ bgcolor: r.available ? '#f0fdf4' : '#f1f5f9', color: r.available ? '#16a34a' : '#64748b', fontWeight: 600, fontSize: '0.7rem' }} /> },
    { name: 'TYPE', minWidth: '130px', cell: r => <Chip label={r.driverType === 'RUSHMYFOOD' ? 'RushMyFood' : 'Restaurant'} size="small" sx={{ bgcolor: r.driverType === 'RUSHMYFOOD' ? '#eff6ff' : '#fef3c7', color: r.driverType === 'RUSHMYFOOD' ? '#3b82f6' : '#d97706', fontWeight: 600, fontSize: '0.7rem' }} /> },
    { name: '', cell: r => <ActionMenu row={r} isDriver />, width: '60px', right: true },
  ]

  const inactiveColumns = [
    {
      name: 'NAME', sortable: true, selector: r => r.firstName, minWidth: '200px',
      cell: r => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#f1f5f920', border: '2px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#94a3b8' }}>
            {roleIcon[r.role] || <RoomServiceIcon sx={{ fontSize: 18 }} />}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#94a3b8' }}>{r.firstName} {r.lastName}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1' }}>{r.email}</Typography>
          </Box>
        </Box>
      )
    },
    { name: 'PHONE', selector: r => r.phone, minWidth: '130px', cell: r => <Typography sx={{ fontSize: '0.83rem', color: '#94a3b8' }}>{r.phone}</Typography> },
    { name: 'ROLE', minWidth: '110px', cell: r => <Chip label={r.role} size="small" sx={{ bgcolor: '#f1f5f9', color: '#94a3b8', fontWeight: 700, fontSize: '0.7rem' }} /> },
    {
      name: 'REMOVED ON', minWidth: '140px',
      cell: r => <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>{r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : '—'}</Typography>
    },
    {
      name: '', minWidth: '130px', right: true,
      cell: r => (
        <Button size="small" variant="outlined" startIcon={<RestoreIcon />}
          onClick={() => handleReactivate(r._id, `${r.firstName} ${r.lastName}`)}
          sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, borderColor: '#3b82f6', color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}>
          Reactivate
        </Button>
      )
    },
  ]

  const customStyles = {
    headCells: { style: { fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', backgroundColor: '#f8fafc' } },
    rows: { style: { borderBottom: '1px solid #f1f5f9', '&:hover': { backgroundColor: '#f8fafc' } } },
  }

  const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.88rem' } }

  return (
    <StaffErrorBoundary>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.75rem', color: '#0f172a' }}>Staff</Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem', mt: 0.5 }}>Manage servers, managers and drivers for your restaurant</Typography>
        </Box>

        {successMsg && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setSuccessMsg('')}>
            {successMsg}
          </Alert>
        )}

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Servers', count: staffList.filter(s => (s.role || '').split(',').includes('SERVER')).length, color: '#3b82f6' },
            { label: 'Managers', count: staffList.filter(s => (s.role || '').split(',').includes('MANAGER')).length, color: '#8b5cf6' },
            { label: 'Cashiers', count: staffList.filter(s => (s.role || '').split(',').includes('CASHIER')).length, color: '#0891b2' },
            { label: 'Kitchen', count: staffList.filter(s => (s.role || '').split(',').includes('KITCHEN')).length, color: '#f59e0b' },
            { label: 'Drivers', count: driverList.length, color: '#f97316' },
          ].map(({ label, count, color }) => (
            <Paper key={label} elevation={0} sx={{ px: 3, py: 2, borderRadius: '16px', border: '1px solid #e2e8f0', minWidth: 120 }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color }}>{count}</Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>{label}</Typography>
            </Paper>
          ))}
        </Box>

        {/* Table card */}
        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Box sx={{ px: 3, pt: 3, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}
              variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
              sx={{ '& .MuiTab-root': { fontSize: '0.8rem', fontWeight: 600, minWidth: 80, textTransform: 'none' } }}>
              <Tab label="All" />
              <Tab label="Server" />
              <Tab label="Manager" />
              <Tab label="Cashier" />
              <Tab label="Kitchen" />
              <Tab label="Driver" />
              <Tab label={`Inactive${inactiveList.length ? ` (${inactiveList.length})` : ''}`} sx={{ color: tab === 6 ? '#ef4444 !important' : undefined }} />
            </Tabs>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <TextField size="small" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
                sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.85rem' } }} />
              {tab !== 5 && tab !== 6 && (
                <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => { setForm(emptyStaffForm); setFormError(''); setAddModal(true) }}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}>
                  Add Staff
                </Button>
              )}
              {(tab === 0 || tab === 5) && (
                <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => { setDriverForm(emptyDriverForm); setDriverFormError(''); setAddDriverModal(true) }}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
                  Add Driver
                </Button>
              )}
            </Box>
          </Box>

          {showStaff && (
            <>
              {tab !== 5 && <Typography sx={{ px: 3, py: 1, fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', borderTop: '1px solid #f1f5f9' }}>SERVERS & MANAGERS</Typography>}
              <DataTable columns={staffColumns} data={filteredStaff} progressPending={staffLoading} customStyles={customStyles} noHeader pagination paginationPerPage={10} />
            </>
          )}

          {showDrivers && (
            <>
              <Typography sx={{ px: 3, py: 1, fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', borderTop: '1px solid #f1f5f9' }}>DRIVERS</Typography>
              <DataTable columns={driverColumns} data={filteredDrivers} progressPending={driverLoading} customStyles={customStyles} noHeader pagination paginationPerPage={10} />
            </>
          )}

          {showInactive && (
            <>
              <Typography sx={{ px: 3, py: 1, fontSize: '0.75rem', fontWeight: 700, color: '#ef4444', letterSpacing: '0.05em', borderTop: '1px solid #f1f5f9' }}>INACTIVE / REMOVED STAFF</Typography>
              <DataTable columns={inactiveColumns} data={filteredInactive} progressPending={inactiveLoading} customStyles={customStyles} noHeader pagination paginationPerPage={10} />
            </>
          )}
        </Paper>

        {/* ── Add Staff Modal ───────────────────────────────────────────────── */}
        <Dialog open={addModal} onClose={() => setAddModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Add Staff Member</Typography>
            <IconButton size="small" onClick={() => setAddModal(false)}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ borderRadius: '10px', mb: 2, mt: 1, fontSize: '0.82rem' }}>
              An invitation email will be sent to the staff member to verify their email and set their own password.
            </Alert>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="First Name" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} fullWidth size="small" sx={inputSx} />
                <TextField label="Last Name" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} fullWidth size="small" sx={inputSx} />
              </Box>
              <TextField label="Email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} fullWidth size="small" sx={inputSx} />
              <TextField label="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} fullWidth size="small" sx={inputSx} />
              <TextField label="Address (optional)" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} fullWidth size="small" sx={inputSx} multiline rows={2} />
              <FormControl size="small" fullWidth>
                <InputLabel>Roles</InputLabel>
                <Select multiple value={form.role} label="Roles" onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                  renderValue={selected => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {selected.map(v => <Chip key={v} label={v} size="small" sx={{ bgcolor: `${roleColor[v]}15`, color: roleColor[v], fontWeight: 700, fontSize: '0.7rem' }} />)}
                    </Box>
                  )}
                  sx={{ borderRadius: '10px' }}>
                  <MenuItem value="SERVER">Server</MenuItem>
                  <MenuItem value="MANAGER">Manager</MenuItem>
                  <MenuItem value="CASHIER">Cashier</MenuItem>
                  <MenuItem value="KITCHEN">Kitchen</MenuItem>
                  <MenuItem value="DRIVER">Driver</MenuItem>
                </Select>
              </FormControl>
              {formError && <Alert severity="error" sx={{ borderRadius: '10px' }}>{formError}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={() => setAddModal(false)} sx={{ borderRadius: '10px', textTransform: 'none' }}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} variant="contained"
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}>
              {creating ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Edit Staff Modal ──────────────────────────────────────────────── */}
        <Dialog open={editModal} onClose={() => setEditModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Edit Staff Member</Typography>
            <IconButton size="small" onClick={() => setEditModal(false)}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="First Name" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} fullWidth size="small" sx={inputSx} />
                <TextField label="Last Name" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} fullWidth size="small" sx={inputSx} />
              </Box>
              <TextField label="Email" value={form.email} disabled fullWidth size="small" sx={inputSx} helperText="Email cannot be changed" />
              <TextField label="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} fullWidth size="small" sx={inputSx} />
              <TextField label="Address (optional)" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} fullWidth size="small" sx={inputSx} multiline rows={2} />
              <FormControl size="small" fullWidth>
                <InputLabel>Roles</InputLabel>
                <Select multiple value={form.role} label="Roles" onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                  renderValue={selected => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {selected.map(v => <Chip key={v} label={v} size="small" sx={{ bgcolor: `${roleColor[v]}15`, color: roleColor[v], fontWeight: 700, fontSize: '0.7rem' }} />)}
                    </Box>
                  )}
                  sx={{ borderRadius: '10px' }}>
                  <MenuItem value="SERVER">Server</MenuItem>
                  <MenuItem value="MANAGER">Manager</MenuItem>
                  <MenuItem value="CASHIER">Cashier</MenuItem>
                  <MenuItem value="KITCHEN">Kitchen</MenuItem>
                  <MenuItem value="DRIVER">Driver</MenuItem>
                </Select>
              </FormControl>
              {formError && <Alert severity="error" sx={{ borderRadius: '10px' }}>{formError}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setEditModal(false)} sx={{ borderRadius: '10px', textTransform: 'none' }}>Cancel</Button>
            <Button onClick={handleEdit} disabled={saving} variant="contained"
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}>
              {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Add Driver Modal ──────────────────────────────────────────────── */}
        <Dialog open={addDriverModal} onClose={() => setAddDriverModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Add Restaurant Driver</Typography>
            <IconButton size="small" onClick={() => setAddDriverModal(false)}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Alert severity="info" sx={{ borderRadius: '10px', fontSize: '0.82rem' }}>
                A temporary password will be emailed to the driver. They log in using the existing <strong>Rider app</strong>.
              </Alert>
              <TextField label="Full Name" value={driverForm.name} onChange={e => setDriverForm(f => ({ ...f, name: e.target.value }))} fullWidth size="small" sx={inputSx} />
              <TextField label="Email" type="email" value={driverForm.email} onChange={e => setDriverForm(f => ({ ...f, email: e.target.value }))} fullWidth size="small" sx={inputSx} />
              <TextField label="Phone" value={driverForm.phone} onChange={e => setDriverForm(f => ({ ...f, phone: e.target.value }))} fullWidth size="small" sx={inputSx} />
              {driverFormError && <Alert severity="error" sx={{ borderRadius: '10px' }}>{driverFormError}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setAddDriverModal(false)} sx={{ borderRadius: '10px', textTransform: 'none' }}>Cancel</Button>
            <Button onClick={handleCreateDriver} disabled={creatingDriver} variant="contained"
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
              {creatingDriver ? <CircularProgress size={18} color="inherit" /> : 'Add Driver'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Promote Driver Modal ──────────────────────────────────────────── */}
        <Dialog open={promoteModal} onClose={() => setPromoteModal(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Promote to RushMyFood Driver</Typography>
            <IconButton size="small" onClick={() => setPromoteModal(false)}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Alert severity="warning" sx={{ borderRadius: '10px', fontSize: '0.82rem' }}>
                <strong>{promoteRider?.name}</strong> will become a RushMyFood platform driver and will no longer be exclusively linked to your restaurant.
              </Alert>
              <FormControl size="small" fullWidth>
                <InputLabel>Assign Zone</InputLabel>
                <Select value={promoteZone} label="Assign Zone" onChange={e => setPromoteZone(e.target.value)} sx={{ borderRadius: '10px' }}>
                  {zones.map(z => <MenuItem key={z._id} value={z._id}>{z.title}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setPromoteModal(false)} sx={{ borderRadius: '10px', textTransform: 'none' }}>Cancel</Button>
            <Button onClick={handlePromote} disabled={!promoteZone} variant="contained"
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}>
              Promote
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Record Payout Dialog ──────────────────────────────── */}
        <Dialog open={payoutModal} onClose={() => setPayoutModal(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Record Tip Payout</Typography>
            <IconButton size="small" onClick={() => setPayoutModal(false)}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent>
            {payoutStaff && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: '10px' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
                  {payoutStaff.firstName} {payoutStaff.lastName}
                </Typography>
                <Box display="flex" gap={1.5} mt={0.5}>
                  <Typography sx={{ fontSize: '0.8rem', color: '#15803d' }}>
                    Earned: ${parseFloat(payoutStaff.totalTipsEarned || 0).toFixed(2)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#1d4ed8' }}>
                    Paid: ${parseFloat(payoutStaff.totalTipsPaid || 0).toFixed(2)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#d97706', fontWeight: 700 }}>
                    Outstanding: ${(parseFloat(payoutStaff.totalTipsEarned || 0) - parseFloat(payoutStaff.totalTipsPaid || 0)).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            )}
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <TextField
                label="Amount" type="number" size="small" fullWidth
                value={payoutAmount} onChange={e => setPayoutAmount(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Method</InputLabel>
                <Select value={payoutMethod} label="Method" onChange={e => setPayoutMethod(e.target.value)} sx={{ borderRadius: '10px' }}>
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Note (optional)" size="small" fullWidth multiline rows={2}
                value={payoutNote} onChange={e => setPayoutNote(e.target.value)}
                placeholder="e.g. Weekly settlement"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
              {payoutError && <Alert severity="error" sx={{ borderRadius: '10px', fontSize: '0.82rem' }}>{payoutError}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setPayoutModal(false)} sx={{ borderRadius: '10px', textTransform: 'none' }}>Cancel</Button>
            <Button onClick={handleRecordPayout} disabled={payingOut} variant="contained"
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, bgcolor: '#15803d', '&:hover': { bgcolor: '#166534' } }}>
              {payingOut ? 'Saving...' : 'Record Payout'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Payout History Dialog ─────────────────────────────── */}
        {historyModal && historyStaff && (
          <PayoutHistoryDialog
            staff={historyStaff}
            query={GET_STAFF_PAYOUTS}
            onClose={() => { setHistoryModal(false); setHistoryStaff(null) }}
          />
        )}
      </Container>
    </StaffErrorBoundary>
  )
}

function PayoutHistoryDialog({ staff, query, onClose }) {
  const restaurantId = localStorage.getItem('restaurantId') || null
  const { data, loading } = useQuery(query, { variables: { staffId: staff._id, restaurantId }, fetchPolicy: 'cache-and-network' })
  const payouts = data?.staffPayouts ?? []
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Payout History</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>{staff.firstName} {staff.lastName}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: '10px', display: 'flex', gap: 3 }}>
          <Box>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>TOTAL EARNED</Typography>
            <Typography sx={{ fontWeight: 700, color: '#15803d' }}>${parseFloat(staff.totalTipsEarned || 0).toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>TOTAL PAID</Typography>
            <Typography sx={{ fontWeight: 700, color: '#1d4ed8' }}>${parseFloat(staff.totalTipsPaid || 0).toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>OUTSTANDING</Typography>
            <Typography sx={{ fontWeight: 700, color: '#d97706' }}>
              ${(parseFloat(staff.totalTipsEarned || 0) - parseFloat(staff.totalTipsPaid || 0)).toFixed(2)}
            </Typography>
          </Box>
        </Box>
        {loading && <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>}
        {!loading && payouts.length === 0 && (
          <Typography sx={{ textAlign: 'center', color: '#94a3b8', py: 3, fontSize: '0.875rem' }}>No payouts recorded yet</Typography>
        )}
        {payouts.map((p, i) => (
          <Box key={p._id}>
            {i > 0 && <Divider />}
            <Box sx={{ py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>
                  ${parseFloat(p.amount).toFixed(2)} — {p.method}
                </Typography>
                {p.note && <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mt: 0.25 }}>{p.note}</Typography>}
              </Box>
              <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', ml: 2, flexShrink: 0 }}>
                {p.paidAt ? new Date(p.paidAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
              </Typography>
            </Box>
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ borderRadius: '10px', textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default Staff
